<template>
  <div class="Events container--ultra-wide">
    <Panel>
      <template slot="body">
        <svg ref="chart"></svg>
        <div ref="tooltip" class="Tooltip"></div>

        <div class="Events-selected">
          <Table>
            <tbody v-if="selectedEvents.length > 0">
              <tr v-for="(event, key) in selectedEvents"
                :style="`background-color: ${event.colour ? `rgba(var(--wc3-${event.colour.toLowerCase ()}), .1)` : 'transparent'}`"
              >
                <td class="w10">{{ time (event.time) }}</td>
                <td class="w15">{{ capitalize (event.eventName) }}</td>

                <template v-for="i in 3">
                  <template v-if="event.event.params [i - 1]">
                    <td>
                      <strong>{{ capitalize (event.event.params [i - 1]) }}</strong>
                    </td>

                    <td>{{ event.args [i - 1] }}</td>
                  </template>

                  <template v-else>
                    <td></td>
                    <td></td>
                  </template>
                </template>
              </tr>
            </tbody>

            <tbody v-else>
              <tr>
                <td colspan="5">No events selected.</td>
              </tr>
            </tbody>
          </Table>
        </div>
      </template>
    </Panel>
  </div>
</template>

<script>
  import * as d3 from 'd3';

  import Panel from '@/legacy/components/widgets/Panel';
  import Table from '@/legacy/components/widgets/Table.vue';

  import { rgba, capitalize, getTextWidth, metaFactory } from '@/legacy/lib//template';
  import { time, forEach, inArray } from '@/legacy/lib//util';

  export default {
    metaInfo () {
      return metaFactory ({
        title: `${this.replay.name} Events`,
        description: `Warcraft III meta data (W3MMD) game events for ${this.replay.name}.`
      });
    },

    components: {
      Panel,
      Table
    },

    props: {
      replay: {
        type: Object
      }
    },

    data: function () {
      return {
        selectedEvents: []
      }
    },

    methods: {
      time,
      capitalize,

      colour (event) {
        if (event.colour) {
          return rgba(`--wc3-${event.colour}`);
        }

        return rgba(`--white`, 0.5);
      }
    },

    mounted: function () {
      let self = this;

      let data = this.replay.getEvents ();

      let svg     = d3.select (this.$refs.chart);
      let tooltip = d3.select (this.$refs.tooltip);

      let lanes = [];
      let events = [];

      let leftMargin = 0;

      let box = svg
        .append ('text')
        .attr ('class', 'box')
        .style ('opacity', 0);

      forEach (data, (event, index) => {
        // leftMargin = Math.max (
        //   leftMargin,
        //   getTextWidth (event.eventName, "20px Arial")
        // );

        box.text (event.eventName);

        leftMargin = Math.max (
          leftMargin,
          box
            .node ()
            .getBBox ()
            .width
        );


        if (!inArray (event.eventName, lanes)) {
          lanes.push (event.eventName);
        }

        event.start = event.time;
        // event.end   = event.start + 1;
        event.lane  = lanes.indexOf (event.eventName);
        event.id    = index;

        events.push (event);
      });

      /** **/

      let margin = {
        top:     20,
        right:   85,
        bottom:  50,
        left:    leftMargin + 45,

        axis: 20
      };

      let boundingBox = svg
        .node ()
        .getBoundingClientRect ();

      let mainLaneHeight = 60;
      let miniLaneHeight = 15;
      let gutterHeight = 50;

      let miniHeight = miniLaneHeight * lanes.length
        + margin.top + margin.bottom;

      let height = mainLaneHeight * lanes.length
        + miniHeight
        + margin.top + margin.bottom;

      let mainHeight = height - miniHeight - margin.top - margin.bottom;

      // let height = lanes.length * laneHeight + margin.top + margin.bottom + miniHeight;

      let width  = boundingBox.width - margin.left - margin.right;
      // let height = boundingBox.height - margin.top - margin.bottom;

      /** **/

      let radius = 10;
      // let scale = (1 / (timeEnd - timeStart)) * width;
      let scale = 0.5;

      let brushInit = [ 0, width ];

      /** **/

      let timeStart = d3.min (events, d => d.start) - radius * 3;
      let timeEnd   = d3.max (events, d => d.start) + radius * 3;

      /** **/

      let x = d3
        .scaleTime ()
        .range ([ 0, width ])
        .domain ([ timeStart, timeEnd ]);

      let x1 = d3
        .scaleLinear ()
        .range ([ 0, width ]);

      let y1 = d3
        .scaleLinear ()
        .range ([ 0, mainHeight ])
        .domain ([ 0, lanes.length ]);

      let y2 = d3
        .scaleLinear ()
        .range ([ 0, miniHeight ])
        .domain ([ 0, lanes.length ]);

      /** **/

      svg
        .attr ('width',  width + margin.right + margin.left)
        .attr ('height', height + margin.top + margin.bottom)
        .attr ('class', 'chart');

      svg
        .append ('defs')
        .append ('clipPath')
        .attr ('id', 'clip')
        .append ('rect')
        .attr ('width', width)
        .attr ('height', mainHeight);

      /** **/

      let axis = d3
        .axisBottom (x1)
        .tickSize (0)
        .tickFormat (d => time (Math.max (0, d)));

      svg
        .append ('g')
        .attr ('class', 'axis axis--x')
        .attr ('transform', `translate(${margin.left}, ${mainHeight + margin.top + margin.axis})`)
        .call (axis);

      /** **/

      let brush = d3
        .brushX ()
        .extent([
          [ 0, 0 ],
          [ width, miniHeight ]
        ])
        .on ('brush', brushed);

      /** **/

      let main = svg
        .append ('g')
        .attr ('transform', `translate(${margin.left}, ${margin.top})`)
        .attr ('width', width)
        .attr ('height', mainHeight)
        .attr ('class', 'main');

      main
        .append ('g')
        .selectAll ('.laneLines')
        .data (lanes)
        .enter ()
        .append ('line')
        .attr ('x1', 0)
        .attr ('y1', (d, i) => y1 (i))
        .attr ('x2', width)
        .attr ('y2', (d, i) => y1 (i))
        .attr ('stroke', rgba('--white', 0.2));

      // main
      //   .append ('line')
      //   .attr ('x1', 0)
      //   .attr ('y1', d => y1 (5))
      //   .attr ('x2', width)
      //   .attr ('y2', d => y1 (5))
      //   .attr ('stroke', 'white');

      main
        .append ('g')
        .selectAll ('.laneText')
        .data (lanes)
        .enter ()
        .append ('text')
        .text (d => capitalize (d))
        .attr ('x', -20)
        .attr ('y', (d, i) => y1 (i + 0.5))
        .attr ('dy', '.5ex')
        .attr ('text-anchor', 'end')
        .attr ('class', 'laneText');

      /** **/

      let itemRects = main
        .append ('g')
        .attr ('clip-path', 'url(#clip)');

      /** **/

      let mini = svg
        .append ('g')
        .attr ('transform', `translate(${margin.left}, ${mainHeight + margin.bottom + margin.axis * 2})`)
        .attr ('width', width)
        .attr ('height', miniHeight)
        .attr ('class', 'mini');

      mini
        .append ('g')
        .selectAll ('.laneLines')
        .data (lanes)
        .enter ()
        .append ('line')
        .attr ('x1', 0)
        .attr ('y1', (d, i) => y2 (i))
        .attr ('x2', width)
        .attr ('y2', (d, i) => y2 (i))
        .attr ('stroke', rgba('--white', 0.2));

      mini
        .append ('g')
        .selectAll ('.laneText')
        .data (lanes)
        .enter ()
        .append ('text')
        .text (d => capitalize (d))
        .attr ('x', -20)
        .attr ('y', (d, i) => y2 (i + 0.5))
        .attr ('dy', '.5ex')
        .attr ('text-anchor', 'end')
        .attr ('class', 'laneText');

      mini
        .append ('g')
        .selectAll ('.miniItem')
        .data (events)
        .enter ()
        // .append ('rect')
        .append ('circle')
        .attr ('class', 'miniItem')
        // .attr ('x', d => x (d.start))
        // .attr ('y', d => y2 (+d.lane + 0.5) - 5)
        .attr ('cx', d => x (d.start))
        .attr ('cy', d => y2 (d.lane) + miniLaneHeight - 1)
        .attr ('r', d => radius * scale)
        .attr ('fill', d => this.colour (d))
        // .attr ('width', d => (d.end - d.start) * scale)
        // .attr ('height', 10);

      mini
        .append ('g')
        .attr ('class', 'x brush')
        .call (brush)
        .call (brush.move, brushInit)
        .selectAll ('rect')
        .attr ('y', 1)
        .attr ('height', miniHeight - 1);

      /** **/

      brushed ();

      /** **/

      function brushed () {
        // if (!d3.event) {
        //   return;
        // }

        let selection = (d3.event && d3.event.selection) || brushInit;
        let timeSelection = selection.map (x.invert, x);

        let minExtent = timeSelection [0];
        let maxExtent = timeSelection [1];

        let visEvents = events.filter (d => d.start <= maxExtent && d.start >= minExtent);

        self.selectedEvents = visEvents;

        x1.domain ([ minExtent, maxExtent ]);

        svg.select ('.axis--x').call (axis);

        let rects = itemRects
          .selectAll ('circle')
          .data (visEvents, d => d.id)
          .attr ('cx', d => x1 (d.start))
          .attr ('r', radius);

        rects
          .enter ()
          .append ('circle')
          .attr ('class', d => 'miniItem')
          .attr ('cx', d => x1 (d.start))
          .attr ('cy', d => y1 (d.lane) + mainLaneHeight / 2 - 1)
          .attr ('fill', d => self.colour (d))
          .attr ('r', radius)
          // .attr ('height', d => y1 (1))
          .on ('mouseover', showTooltip)
          .on ('mouseout', hideTooltip);

        rects
          .exit ()
          .remove ();
      }

      function showTooltip (d) {
        let content = '';

        content += '<table class="Definitions">';

        content += `
          <tr>
            <th>Time</th>
            <td>${time (d.start)}</td>
          </tr>
        `;

        for (let key in d.event.params) {
          let param = capitalize (d.event.params [key]);
          let arg   = d.args [key];

          content += `
            <tr>
              <th>${param}</th>
              <td>${arg}</td>
            </tr>
          `;
        }

        content += '</table>'

        tooltip
          .html (content)
          .style ('display', 'block');

        let boundingBox = tooltip
          .node ()
          .getBoundingClientRect ();

        let left = +this.getAttribute ('cx') + margin.left - boundingBox.width / 2 + 30;
        let top  = +this.getAttribute ('cy') + margin.top - boundingBox.height + 15;

        tooltip
          .style ('left', `${left}px`)
          .style ('top', `${top}px`);
      }

      function hideTooltip (d) {
        tooltip
          .style ('display', 'none');
      }
    }
  };
</script>

<style lang="scss">
  .Events {
    position: relative;
    height: 100%;

    svg {
      width: 100%;
      height: 100%;
    }

    text {
      fill: rgba(var(--white), 1);
    }

    .chart {
      shape-rendering: crispEdges;
    }

    .mini text {
      font-size: fetch(font s);
    }

    .box,
    .main text {
      font-size: fetch(font m);
    }

    .selection {
      fill: rgba(var(--white), .4);
      stroke: rgba(var(--white), .6);
    }

    .Tooltip {
      // max-width: 300px;
      word-wrap: break-word;
      white-space: normal;
    }

    .Events-selected {
      padding: 10px 40px;

      strong {
        display: inline-block;
        width: 80px;
        margin: 0 15px;
      }
    }
  }
</style>